<template>
  <div class="order-account-view">
    <QS-scroller-box>
      <QS-h4-title title="退车状态" />
      <form-text-box
        :column="3"
        :dataList="orderInfoConfig.list"
        :model="orderInfoConfig.model"
      />

      <QS-h4-title title="违章/事故/维保信息" />
      <form-text-box
        :column="3"
        :dataList="mergeInfoConfig.list"
        :model="mergeInfoConfig.model"
      />

      <template v-if="resultType">
      <QS-h4-title title="核算结果" />
      <div>核算结果：{{accountResultText}}</div>
      <QS-common-table
        class="mt12"
        :data="resultItemList"
        :columns="accountResultColumns"
        emptyText="暂无核算结果"
        :pagination="false"
      >
      </QS-common-table>
      </template>

      <QS-h4-title class="mt24" title="核算明细" />
      <el-alert
        title="以下款项信息，为该订单在核算完成后，提交的明细信息的快照。"
        type="info"
        :show-icon="false"
        :closable="false">
      </el-alert>

      <el-alert
        class="mt24"
        title="抵扣明细"
        type="info"
        :show-icon="false"
        :closable="false">
      </el-alert>
      <QS-common-table
        :data="paymentList"
        :columns="paymentColumns"
        emptyText="暂无抵扣明细"
        :pagination="false"
      >
      </QS-common-table>

      <el-alert
        class="mt24"
        title="收款明细"
        type="info"
        :show-icon="false"
        :closable="false">
      </el-alert>
      <QS-common-table
        :data="receivableList"
        :columns="receivableColumns"
        emptyText="暂无收款明细"
        :pagination="false"
      >
      </QS-common-table>

      <!-- 只有“待审批”才展示“查看审批状态” -->
      <!-- state: 核算状态，null-没有核算信息 0-待核算 1-已完成 2-待审批 3-核算中 4-核算失败 -->
      <template
        slot="bottomAction"
        v-if="displayOrderAccountDetail && displayOrderAccountDetail.state === 2"
      >
        <div class="btn-group">
          <el-button @click="handleCancel">返回</el-button>
          <el-button @click="handleLookApproval">查看审批动态</el-button>
        </div>
      </template>
      <LookApprovalDetail ref="lookApprovalDetail" :bizId="displayOrderAccountDetail && displayOrderAccountDetail.orderId" :bizType="orderAccountBizType"></LookApprovalDetail>
    </QS-scroller-box>
  </div>
</template>

<script>
import { getOrderAccountSensitive, getOrderAccountState } from '@/api/domain/orders'
import LookApprovalDetail from '@/pages/approvals/components/lookApprovalDetail'
import { convertNumber } from '@/utils/'

// 结果类型
const RESULT_TYPE = {
  // 应收
  Input: 1,
  // 应付
  Output: 2,
}

// 文案
const TEXT = {
  FeeType: {
    [RESULT_TYPE.Input]: '收款项（向客户收款）',
    [RESULT_TYPE.Output]: '付款项（向客户退款）',
  },
  CustomerName: {
    [RESULT_TYPE.Input]: '付款方',
    [RESULT_TYPE.Output]: '收款方',
  },
  AccruedDate: {
    [RESULT_TYPE.Input]: '应收日期',
    [RESULT_TYPE.Output]: '应付日期',
  },
  AccruedAmount: {
    [RESULT_TYPE.Input]: '应收金额',
    [RESULT_TYPE.Output]: '应付金额',
  },
}

export default {
  components: {
    LookApprovalDetail,
  },
  props: {
    orderId: {
      type: [Number, String],
    },
    orderAccountDetail: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // 订单信息
      orderInfo: {
        plateNo: '', // 车牌号
        customerName: '', // 客户姓名
        orderState: '', // 订单状态
        returnCarType: '', // 退车类型
        returnCarReason: '', // 退车原因
        inventoryState: '', // 库存状态
        carTakeOverTime: '', // 车辆交接完成时间
      },
      // 违章/维保/事故信息
      mergeInfo: {
        violationCount: 0, // 违章数量
        violationHandledCount: 0, // 违章已处理数量
        maintainCount: 0, // 维保数量
        maintainedCount: 0, // 维保已处理数量
        accidentCount: 0, // 事故数量
        accidentHandledCount: 0, // 事故已处理数量
      },
      resultType: '', // 核算结果类型（1-应收 2-应付）
      resultAmount: 0, // 核算结果金额
      resultItemList: [], // 核算结果明细
      paymentList: [], // 费用核算付款项
      receivableList: [], // 费用核算收款项
      paymentColumns: [ // 付款项列表配置
        {
          name: 'feeName',
          columnProps: {
            label: '抵扣项',
            prop: 'feeName',
          },
        },
        {
          name: 'payAmount',
          columnProps: {
            label: '应付金额',
            prop: 'payAmount',
          },
          columnHeaderTooltip: '订单核算时，对应款项下，所有已完成+未完成（不包含已关闭）的应付单的应付金额总和',
          render: ({ row }) => {
            return `${convertNumber(row.payAmount)}元`
          },
        },
        {
          name: 'paidAmount',
          columnProps: {
            label: '已付金额',
            prop: 'paidAmount',
          },
          columnHeaderTooltip: '订单核算时，对应款项下，所有已完成+未完成（不包含已关闭）的应付单的已付金额总和',
          render: ({ row }) => {
            return `${convertNumber(row.paidAmount)}元`
          },
        },
        {
          name: 'payingAmount',
          columnProps: {
            label: '付款中金额',
            prop: 'payingAmount',
          },
          columnHeaderTooltip: '订单核算时，对应款项下，所有已完成+未完成（不包含已关闭）的应付单的付款中金额总和。付款中的状态是指有正在进行中（在审批中）的付款单。',
          render: ({ row }) => {
            return `${convertNumber(row.payingAmount)}元`
          },
        },
        {
          name: 'openDeduct',
          columnProps: {
            label: '是否参与抵扣',
            prop: 'openDeduct',
          },
          columnHeaderTooltip: '展示订单核算时的抵扣配置，参与抵扣的付款项会自动抵扣应收款项。',
          render: ({ row }) => {
            return row.openDeduct ? '是' : '否'
          },
        },
        {
          name: 'deductAmount',
          columnProps: {
            label: '抵扣金额',
            prop: 'deductAmount',
          },
          columnHeaderTooltip: '订单核算后，对应款项下的实际抵扣金额',
          render: ({ row }) => {
            return `${convertNumber(row.deductAmount)}元`
          },
        },
        {
          name: 'remarks',
          columnProps: {
            label: '备注',
            prop: 'remarks',
          },
          render: ({ row }) => {
            return row.remarks || '--'
          },
        },
        {
          name: 'photo',
          columnProps: {
            label: '备注图片',
            prop: 'photo',
            width: 320,
          },
          render: ({ row }) => {
            return this.$createElement('div', {
              class: {
                'photo-list': true,
              },
            }, (row.photo || []).map((item) => {
              return this.$createElement('EnlargeImg', {
                class: {
                  'photo-item': true,
                },
                props: {
                  imgSrc: item,
                },
              })
            }))
          },
        },
      ],
      receivableColumns: [ // 收款项列表配置
        {
          name: 'feeName',
          columnProps: {
            label: '收款项',
            prop: 'feeName',
          },
        },
        {
          name: 'receivableAmount',
          columnProps: {
            label: '应收金额',
            prop: 'receivableAmount',
          },
          columnHeaderTooltip: '订单核算时，对应收款项下，所有已完成+未完成（不包含已关闭）的应收单的应收金额总和',
          render: ({ row }) => {
            if (!row.feeState) {
              return '无需收款'
            }
            return `${convertNumber(row.receivableAmount)}元`
          },
        },
        {
          name: 'receivedAmount',
          columnProps: {
            label: '已收金额',
            prop: 'receivedAmount',
          },
          columnHeaderTooltip: '订单核算时，对应收款项下，所有已完成+未完成（不包含已关闭）的应收单的已收金额总和',
          render: ({ row }) => {
            if (!row.feeState) return
            return `${convertNumber(row.receivedAmount)}元`
          },
        },
        {
          name: 'receivingAmount',
          columnProps: {
            label: '收款中金额',
            prop: 'receivingAmount',
          },
          columnHeaderTooltip: '订单核算时，对应收款项下，所有已完成+未完成（不包含已关闭）的应收单的收款中金额总和。收款中的状态是指有正在进行中（在审批中）的收款单。',
          render: ({ row }) => {
            if (!row.feeState) return
            return `${convertNumber(row.receivingAmount)}元`
          },
        },
        {
          name: 'surplusAmount',
          columnProps: {
            label: '剩余应收金额',
            prop: 'surplusAmount',
          },
          columnHeaderTooltip: '应收金额-已收金额-收款中金额',
          render: ({ row }) => {
            if (!row.feeState) return
            return `${convertNumber(row.surplusAmount)}元`
          },
        },
        {
          name: 'remarks',
          columnProps: {
            label: '备注',
            prop: 'remarks',
          },
          render: ({ row }) => {
            return row.remarks || '--'
          },
        },
        {
          name: 'photo',
          columnProps: {
            label: '备注图片',
            prop: 'photo',
            width: 320,
          },
          render: ({ row }) => {
            return this.$createElement('div', {
              class: {
                'photo-list': true,
              },
            }, (row.photo || []).map((item) => {
              return this.$createElement('EnlargeImg', {
                class: {
                  'photo-item': true,
                },
                props: {
                  imgSrc: item,
                },
              })
            }))
          },
        },
      ],
      orderAccountBizType: 17, // 订单核算的审批流类型
      localOrderAccountDetail: {}, // 本地核算结果信息（当未传入时使用）
      pageLoading: null,
    }
  },
  computed: {
    displayOrderAccountDetail() {
      return this.orderAccountDetail && Object.keys(this.orderAccountDetail).length
        ? this.orderAccountDetail
        : this.localOrderAccountDetail
    },
    // 订单信息展示配置
    orderInfoConfig() {
      return {
        list: [
          {
            el: 'text',
            itemConfig: {
              label: '订单状态:',
              prop: 'orderState',
            },
          },
          {
            el: 'text',
            itemConfig: {
              label: '退车类型:',
              prop: 'returnCarType',
            },
          },
          {
            el: 'text',
            itemConfig: {
              label: '退车原因:',
              prop: 'returnCarReason',
            },
          },
          {
            el: 'text',
            itemConfig: {
              label: '库存状态:',
              prop: 'inventoryState',
            },
          },
          {
            el: 'text',
            itemConfig: {
              label: '车辆交接完成时间:',
              prop: 'carTakeOverTime',
            },
          },
        ],
        model: {
          orderState: this.orderInfo.orderState,
          returnCarType: this.orderInfo.returnCarType,
          returnCarReason: this.orderInfo.returnCarReason,
          inventoryState: this.orderInfo.inventoryState,
          carTakeOverTime: this.orderInfo.carTakeOverTime,
        },
      }
    },
    // 违章/事故/维保信息展示配置
    mergeInfoConfig() {
      return {
        list: [
          {
            el: 'input',
            itemConfig: {
              label: '违章',
              prop: 'violationCount',
            },
            render: (createElement) => {
              return createElement('el-form-item', {
                props: {
                  label: '违章:',
                },
              }, [
                createElement('el-button', {
                  props: {
                    type: 'text',
                  },
                  nativeOn: {
                    click: (e) => {
                      e.stopPropagation()
                      this.toViolationRecord()
                    },
                  },
                }, `数量${this.mergeInfo.violationCount}（已处理${this.mergeInfo.violationHandledCount}）`),
              ])
            },
          },
          {
            el: 'input',
            itemConfig: {
              label: '事故',
              prop: 'accidentCount',
            },
            render: (createElement) => {
              return createElement('el-form-item', {
                props: {
                  label: '事故:',
                },
              }, [
                createElement('el-button', {
                  props: {
                    type: 'text',
                  },
                  nativeOn: {
                    click: (e) => {
                      e.stopPropagation()
                      this.toAccidentList()
                    },
                  },
                }, `数量${this.mergeInfo.accidentCount}（已处理${this.mergeInfo.accidentHandledCount}）`),
              ])
            },
          },
          {
            el: 'input',
            itemConfig: {
              label: '维保',
              prop: 'maintainCount',
            },
            render: (createElement) => {
              return createElement('el-form-item', {
                props: {
                  label: '维保:',
                },
              }, [
                createElement('el-button', {
                  props: {
                    type: 'text',
                  },
                  nativeOn: {
                    click: (e) => {
                      e.stopPropagation()
                      this.toMaintainList()
                    },
                  },
                }, `数量${this.mergeInfo.maintainCount}（已处理${this.mergeInfo.maintainedCount}）`),
              ])
            },
          },
        ],
        model: {
          violationCount: this.mergeInfo.violationCount,
          violationHandledCount: this.mergeInfo.violationHandledCount,
          maintainCount: this.mergeInfo.maintainCount,
          maintainedCount: this.mergeInfo.maintainedCount,
          accidentCount: this.mergeInfo.accidentCount,
          accidentHandledCount: this.mergeInfo.accidentHandledCount,
        },
      }
    },
    // 核算结果款项列表配置
    accountResultColumns() {
      return [
        {
          name: 'feeType',
          columnProps: {
            label: TEXT.FeeType[this.resultType] || '',
            prop: 'feeType',
          },
        },
        {
          name: 'plateNo',
          columnProps: {
            label: '车牌号',
            prop: 'plateNo',
            width: 100,
          },
        },
        {
          name: 'customerName',
          columnProps: {
            label: TEXT.CustomerName[this.resultType] || '',
            prop: 'customerName',
          },
        },
        {
          name: 'accruedDate',
          columnProps: {
            label: TEXT.AccruedDate[this.resultType] || '',
            prop: 'accruedDate',
          },
        },
        {
          name: 'accruedAmount',
          columnProps: {
            label: TEXT.AccruedAmount[this.resultType] || '',
            prop: 'accruedAmount',
          },
          render: ({ row }) => `${convertNumber(row.accruedAmount)}元`,
        },
      ]
    },
    // 核算结果文案
    accountResultText() {
      if (!this.resultType) return ''
      return `应向客户${this.resultType === 1 ? '收款' : '退款'} ${convertNumber(this.resultAmount)} 元`
    },
  },
  methods: {
    openPageLoading() {
      this.pageLoading = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'QS-icon-loading',
        background: 'rgba(255, 255, 255, 0.7)',
      })
    },
    closePageLoading() {
      this.pageLoading.close()
    },
    // 获取核算信息
    async getOrderAccountState() {
      const { code, data } = await getOrderAccountState({
        orderId: this.orderId,
      })
      if (data && data.state === 5) {
        this.$message.success('司机线上支付的资金正在原路退还中，请稍后刷新页面进行核算')
      }

      if (code === 1) {
        this.localOrderAccountDetail = data
      }
    },
    // 获取订单核算详情
    async getOrderAccount() {
      let res

      this.openPageLoading()
      try {
        res = await getOrderAccountSensitive({
          orderId: this.orderId,
        })
      } catch (err) {
        console.error(err)
      } finally {
        this.closePageLoading()
      }

      if (!res) return

      const { code, data } = res

      if (code === 1) {
        const orderInfo = data.orderInfo || {}
        const mergeInfo = data.mergeInfo || {}

        this.orderInfo.plateNo = orderInfo.plateNo || ''
        this.orderInfo.customerName = orderInfo.customerName || ''
        this.orderInfo.orderState = orderInfo.orderState || ''
        this.orderInfo.returnCarType = orderInfo.returnCarType || ''
        this.orderInfo.returnCarReason = orderInfo.returnCarReason || ''
        this.orderInfo.inventoryState = orderInfo.inventoryState || ''
        this.orderInfo.outStockTime = orderInfo.outStockTime || ''
        this.orderInfo.carTakeOverTime = orderInfo.carTakeOverTime || ''

        this.mergeInfo.violationCount = mergeInfo.violationCount || 0
        this.mergeInfo.violationHandledCount = mergeInfo.violationHandledCount || 0
        this.mergeInfo.maintainCount = mergeInfo.maintainCount || 0
        this.mergeInfo.maintainedCount = mergeInfo.maintainedCount || 0
        this.mergeInfo.accidentCount = mergeInfo.accidentCount || 0
        this.mergeInfo.accidentHandledCount = mergeInfo.accidentHandledCount || 0

        this.resultType = data.resultType || ''
        this.resultAmount = data.resultAmount || ''
        this.resultItemList = data.accountResultList || []
        // 付款明细图片解析处理
        this.paymentList = (data.paymentItemList || []).map((item) => {
          item.photo = this.getEchoUploadFileData(item.photo, true)
          return item
        })
        // 收款明细图片解析处理
        this.receivableList = (data.receivableItemList || []).map((item) => {
          item.photo = this.getEchoUploadFileData(item.photo, true)
          return item
        })
      }
    },
    // 跳转违章记录列表
    toViolationRecord() {
      this.$router.push({
        path: '/vehicle/violation/record',
        query: {
          orderId: this.orderId,
        },
      })
    },
    // 跳转事故管理列表
    toAccidentList() {
      this.$router.push({
        path: '/vehicle/accident/list',
        query: {
          orderId: this.orderId,
        },
      })
    },
    // 跳转维保管理列表
    toMaintainList() {
      this.$router.push({
        path: '/vehicle/maintenance/list',
        query: {
          orderId: this.orderId,
        },
      })
    },
    // 取消
    handleCancel() {
      this.$emit('cancel')
    },
    // 查看审批动态
    handleLookApproval() {
      this.$refs.lookApprovalDetail.dialogVisible = true
    },
  },
  created() {
    this.getOrderAccount()
  },
  watch: {
    loading(newVal) {
      if (newVal === false && (!this.orderAccountDetail || Object.keys(this.orderAccountDetail).length === 0)) {
        this.getOrderAccountState()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.photo-list {
  display: flex;
  flex-wrap: wrap;
  .photo-item {
    margin-right: 16px;
    width: 80px;
    height: 80px;
  }
}
.basic-list {
  margin-top: 0;
}
.mt12 {
  margin-top: 12px;
}
.mt24 {
  margin-top: 24px;
}
</style>
